import React, { useState, useEffect, useCallback, useContext } from "react";
import IGetCategoriesDTO from "../../../models/DTO/IGetCategoriesDTO";
import IOnGetCategoriesDTO from "../../../models/DTO/IOnGetCategoriesDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICatalog_Item from "../../../models/ICatalog_Item";
import ICatalogCategoryViewerDetail from "../../../models/ICatalogCategoryViewerDetail";
import ICategoryUI from "../../../models/ICategoryUI";
import INotification from "../../../models/INotification";
import IOrder from "../../../models/IOrder";
import NotificationDismissibleEnum from "../../../models/NotificationDismissibleEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import { LoadingResults } from "../../shared/LoadingResults/LoadingResults";
import OwTitle from "../../shared/OwTitle/OwTitle";
import CatalogViewerCart from "../CatalogViewerCart/CatalogViewerCart";
import OwCatalogCategoryRow from "../OwCatalogCategoryRow/OwCatalogCategoryRow";

import "./OwCatalogCategoryViewer.scss";
import IWarehouse from "../../../models/IWarehouse";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import VendorContext from "@contexts/VendorContext";
import UiContext from "@contexts/UiContext";
import { IAddBulkItems } from "@models/IAddBulkItems";
import { propTypes } from "react-bootstrap/esm/Image";

type Props = {
    cancel: () => void;
    catalogGuid: string;
    addToOrder: (items: IAddBulkItems[]) => void;
    order: IOrder;
    warehouses: IWarehouse[];
    // isReadOnly: boolean;
};

const OwCatalogCategoryViewer: React.FC<Props> = ({ cancel, catalogGuid, addToOrder, order, warehouses }) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [rootCategories, setRootCategories] = useState<ICategoryUI[]>([]);
    const [subCategories, setSubCategories] = useState<{ [key: string]: any }>({});
    const [getRootCategories, setGetRootCategories] = useState<boolean>(false);
    const [categoryCatalogItems, setCategoryCatalogItems] = useState<ICatalogCategoryViewerDetail>({});
    const [cartItems, setCartItems] = useState<IAddBulkItems[]>([]);
    const [loadedInitial, setLoadedInitial] = useState<boolean>(false);

    const onGetCategories = useCallback(
        (data: IOnGetCategoriesDTO) => {
            if (getRootCategories) {
                const rootCategoriesData = data.categories.map((category) => {
                    return {
                        ...category,
                        isOpen: false,
                    } as ICategoryUI;
                });

                if (supplierModel.has_new_releases_category) {
                    const newReleasesCategory = {
                        guid: "NEW_RELEASES",
                        id: 0,
                        isOpen: false,
                        name: "New Releases",
                        created_at: "",
                        updated_at: "",
                    } as ICategoryUI;

                    rootCategoriesData.unshift(newReleasesCategory);
                }

                setRootCategories(rootCategoriesData);
                setGetRootCategories(false);
                setLoadedInitial(true);
            }
        },
        [getRootCategories, supplierModel.has_new_releases_category]
    );

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CategoriesOnGetCategories, fn: onGetCategories }];

    const getRootCategoriesHandler = useCallback(() => {
        const payload: IGetCategoriesDTO = {
            userToken: userModel.token,
            userGuid: userModel.guid,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            catalogGuid: catalogGuid,
            onlyRootCategories: true,
        };

        socket.emit(EndpointsEnum.CategoriesGetCategories, payload);
        setGetRootCategories(true);
    }, [userModel.token, userModel.guid, supplierModel.guid, vendorModel.guid, catalogGuid, socket]);

    const removeCartItem = useCallback((item: ICatalog_Item, index: number) => {
        setCartItems((prevCartItems) => {
            const newCartItems = [...prevCartItems];
            newCartItems.splice(index, 1);
            return newCartItems;
        });
    }, []);

    const addCartToOrder = useCallback(() => {
        addToOrder(cartItems);
        cancel();
    }, [addToOrder, cartItems, cancel]);

    const addToCart = (items: IAddBulkItems[]) => {
        const itemsToAdd: IAddBulkItems[] = [];

        for (const newItem of items) {
            let found = false;

            if (supplierModel.no_dupe_order_items) {
                for (const existingItem of order.Order_Items) {
                    if (existingItem.catalog_item_id === newItem.item.catalog_item_id) {
                        found = true;
                    }
                }
            }

            if (!found) {
                itemsToAdd.push(newItem);
            } else {
                const notification: INotification = {
                    title: "item_already_in_cart",
                    message: newItem.item.Catalog_Item?.name || "",
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.error,
                };

                uiContext.notifyFn(null, notification, false);
            }
        }

        const updatedItemsToAdd = [...cartItems, ...itemsToAdd];
        setCartItems(updatedItemsToAdd);
    };

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, [socket, onGetCategories, getRootCategoriesHandler, warehouses]);

    useEffect(() => {
        getRootCategoriesHandler();
    }, []);

    return (
        <div className="OwCatalogCategoryViewer d-flex flex-column" style={{ height: "100vh", overflow: "hidden" }}>
            <div className={"d-flex flex-column p-2 "} style={{ flexShrink: 0 }}>
                <div className="text-end">
                    <button className="btn btn-danger btn-sm" onClick={cancel}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <div className={"d-flex align-items-center"}>
                    <OwTitle className={"me-auto"} leftIconName={"far fa-sitemap"} title={"category_viewer"} />
                </div>
                <hr />
            </div>

            {!loadedInitial ? <LoadingResults /> : null}

            <div className="d-flex flex-column flex-grow-1 position-relative" style={{ minHeight: 0 }}>
                <div className="overflow-auto p-2 h-100" style={{ paddingBottom: "80px" }}>
                    {rootCategories
                        .filter((item) => !item.hide_category)
                        .map((category, index) => {
                            return (
                                <OwCatalogCategoryRow
                                    key={index}
                                    addToCart={addToCart}
                                    socket={socket}
                                    userModel={userModel}
                                    supplierModel={supplierModel}
                                    vendorModel={vendorModel}
                                    category={category}
                                    order={order}
                                    notifyFn={uiContext.notifyFn}
                                    warehouses={warehouses}
                                />
                            );
                        })}
                </div>

                <div className="position-absolute bottom-0 start-0 end-0" style={{ backgroundColor: "#2c2c2c" }}>
                    <CatalogViewerCart
                        order={order}
                        items={cartItems}
                        removeCartItem={removeCartItem}
                        addCartToOrder={addCartToOrder}
                        supplierModel={supplierModel}
                        userModel={userModel}
                    />
                </div>
            </div>
        </div>
    );
};

export default OwCatalogCategoryViewer;
